/**
 * A 3D force-directed layout: repulsion between every node, springs along every edge, and a weak
 * pull toward the origin.
 *
 * Written rather than taken from d3, which only solves two dimensions - and the shape of the
 * integrator is the part worth owning anyway. It follows d3-force's velocity Verlet scheme
 * deliberately, because the alternative (moving straight down the force vector) oscillates: a
 * spring overshoots, the next step overshoots back, and a graph with hubs never settles. Carrying
 * velocity and decaying it damps that out.
 *
 * Pure and synchronous. The caller decides when to run it, which is what lets a story memoise one
 * layout per dataset instead of animating a simulation - a settled layout is easier to read than a
 * drifting one, and it screenshots.
 */

import type { Link } from '../graph/topology';
import type { Vec3 } from '../types';
import { accumulateRepulsion, createOctreeBuilder } from './octree';

export type ForceLayoutArgs = {
  nodeCount: number;
  links: readonly Link[];
  /**
   * Per-node repulsion charge.
   *
   * Given by the caller rather than derived, because what should push hardest is a question about
   * the data: scaling charge with degree spreads hubs apart and lets their neighborhoods breathe,
   * which is the difference between a readable estate and a ball of wool.
   */
  charges: readonly number[];
  iterations: number;
  seed: number;
  repulsion: number;
  /** Rest length of an edge spring, in the same units as the output. */
  linkDistance: number;
  /** 0 to 1. Above 1 a spring overshoots faster than the decay can damp. */
  linkStrength: number;
  /** Pull toward the origin, which is what stops disconnected components drifting apart. */
  gravity: number;
  /** Barnes-Hut opening angle. 0 is exact all-pairs. */
  theta: number;
  /**
   * Holds z at these values and solves only x and y.
   *
   * What makes the layered layout the same solver rather than a second one: planes are a
   * constraint on a 3D layout, not a different algorithm.
   */
  fixedZ: readonly number[] | null;
};

/** Matches d3-force's default, and for the same reason: it damps overshoot without crawling. */
const VELOCITY_DECAY = 0.4;

const ALPHA_MINIMUM = 0.001;

/** Floor on the distance between two bodies, so coincident nodes push apart rather than diverge. */
const MINIMUM_DISTANCE = 0.02;

/**
 * Ceiling on how far a node may move in one iteration, as a fraction of the spring rest length.
 *
 * The safety net under the whole solve, and not a tuning dial. A spring-and-charge system
 * integrated with a fixed step is only conditionally stable: past some gain the correction
 * overshoots by more than the error it was correcting, and the next step overshoots further.
 * Nothing about the parameters makes that visible - the layout simply returns positions in the
 * 1e14 range, which normalize to a single point, and the view draws blank.
 *
 * Capping the step makes divergence impossible rather than unlikely: positions can grow by at most
 * this much per iteration whatever the forces say, so a badly chosen `repulsion` or `linkStrength`
 * costs a layout that has not settled instead of one that is NaN. The same device as
 * Fruchterman-Reingold's cooling temperature, which bounds displacement for the same reason.
 */
const MAXIMUM_STEP_FRACTION = 0.45;

/**
 * Seeds positions evenly rather than at random.
 *
 * A random cloud starts with clumps, and a force layout cannot tell a clump it began with from a
 * cluster in the data - the first few iterations spend their energy undoing the seed. An even
 * distribution starts from no structure at all, so whatever structure appears came from the edges.
 * The radius grows with the count, which is what keeps the starting density - and so the number of
 * iterations needed - roughly independent of size.
 *
 * A planar solve gets a planar seed, and not the sphere flattened onto the plane. The sphere's
 * spiral puts its first and last point at the poles, where the ring radius is zero - so with z
 * held, those two nodes seed at exactly the same (x, y). Two coincident bodies generate a zero
 * delta and therefore no repulsion at all, so they never separate: the layered view drew its first
 * and last node permanently on top of each other.
 */
const seedPositions = (
  nodeCount: number,
  seed: number,
  fixedZ: readonly number[] | null
): Float32Array => {
  const positions = new Float32Array(nodeCount * 3);
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  /* Rotates the whole spiral so a changed seed gives a different starting orientation without
     changing its evenness. */
  const phase = ((seed % 1000) / 1000) * Math.PI * 2;

  if (fixedZ) {
    /* Vogel's spiral: radius as the square root of the index, which spaces points evenly over a
       disc rather than crowding the center. No index shares a radius with another, so no two
       bodies can coincide however the angles fall. */
    const spacing = 1.6;

    for (let index = 0; index < nodeCount; index++) {
      const radius = spacing * Math.sqrt(index + 0.5);
      const angle = index * goldenAngle + phase;

      positions[index * 3] = Math.cos(angle) * radius;
      positions[index * 3 + 1] = Math.sin(angle) * radius;
      positions[index * 3 + 2] = fixedZ[index];
    }

    return positions;
  }

  const radius = Math.cbrt(Math.max(1, nodeCount)) * 1.4;

  for (let index = 0; index < nodeCount; index++) {
    const height = nodeCount === 1 ? 0 : 1 - (2 * index) / (nodeCount - 1);
    const ring = Math.sqrt(Math.max(0, 1 - height * height));
    const angle = index * goldenAngle + phase;

    positions[index * 3] = Math.cos(angle) * ring * radius;
    positions[index * 3 + 1] = Math.sin(angle) * ring * radius;
    positions[index * 3 + 2] = height * radius;
  }

  return positions;
};

/**
 * A force solve in progress, advanced in batches by whoever is drawing it.
 *
 * Resumable because the alternative at a few hundred nodes is a freeze: the solve is ~2.1s at 754
 * nodes, and run to completion in one call it holds the main thread for all of it. Re-running a
 * longer solve each frame would be the same picture at quadratic cost, so the state has to carry.
 */
export type ForceSolve = {
  /** Runs up to `steps` more iterations, stopping at the total the solve was created for. */
  advance: (steps: number) => void;
  /** Iterations still to run; 0 once settled. */
  readonly remaining: number;
  /** Where the marks are as of now, settled or not. */
  toPositions: () => Vec3[];
};

export const createForceSolve = ({
  nodeCount,
  links,
  charges,
  iterations,
  seed,
  repulsion,
  linkDistance,
  linkStrength,
  gravity,
  theta,
  fixedZ,
}: ForceLayoutArgs): ForceSolve => {
  const positions = seedPositions(nodeCount, seed, fixedZ);
  const velocities = new Float32Array(nodeCount * 3);
  const chargeArray = new Float32Array(nodeCount);
  for (let index = 0; index < nodeCount; index++) {
    chargeArray[index] = Math.max(0.1, charges[index] ?? 1);
  }

  /**
   * How many springs pull on each node.
   *
   * Read two ways below, and both matter. It decides which endpoint of a spring moves - a leaf
   * should travel to its hub rather than drag the hub to it - and it is what keeps the total pull
   * on any one node bounded, however many springs it carries. A hub with a hundred links takes
   * roughly a hundredth of each correction, so the hundred of them sum to about one.
   */
  const linkDegrees = new Array<number>(nodeCount).fill(0);
  for (const { source, target } of links) {
    linkDegrees[source] += 1;
    linkDegrees[target] += 1;
  }

  const builder = createOctreeBuilder();
  const forces = new Float32Array(nodeCount * 3);
  const alphaDecay = 1 - ALPHA_MINIMUM ** (1 / Math.max(1, iterations));
  let alpha = 1;

  /**
   * Born settled on an empty graph, which the one-shot form used to express as an early return.
   *
   * Not only a shortcut: a caller driving this a batch per frame would otherwise schedule every
   * one of the iterations to move nothing, so an empty view would animate for a second before
   * agreeing it had nothing to draw.
   */
  let settled = nodeCount === 0 ? iterations : 0;

  /**
   * One iteration, which is the whole of the loop this used to run to completion.
   *
   * Extracted so the solve can be advanced in batches rather than driven to the end in one
   * call. Every array it touches is allocated above and reused, so a batch costs exactly what
   * the same steps cost inside a single loop - the slicing buys responsiveness, not work.
   */
  const step = (): void => {
    alpha -= alpha * alphaDecay;
    forces.fill(0);

    const tree = builder.build(positions, chargeArray, nodeCount);
    for (let index = 0; index < nodeCount; index++) {
      accumulateRepulsion(
        tree,
        positions,
        index,
        repulsion * alpha,
        theta,
        MINIMUM_DISTANCE,
        forces
      );
    }

    for (const { source, target } of links) {
      const deltaX = positions[target * 3] - positions[source * 3];
      const deltaY = positions[target * 3 + 1] - positions[source * 3 + 1];
      const deltaZ = positions[target * 3 + 2] - positions[source * 3 + 2];
      const distance = Math.max(
        MINIMUM_DISTANCE,
        Math.hypot(deltaX, deltaY, deltaZ)
      );

      const pull =
        ((distance - linkDistance) / distance) * linkStrength * alpha;

      /* The correction is split between the endpoints in inverse proportion to how many springs
       each carries, so a leaf travels to its hub rather than the two meeting in the middle.
       Taken from d3-force, and it does two jobs at once: the asymmetry that makes a hub's
       neighborhood readable, and the normalization that stops a hub accumulating one full
       correction per link.

       The previous form - one shared coefficient of `1 / min(degree)` - had neither property. A
       leaf-to-leaf spring got a coefficient of 1 applied to *both* ends, so with velocity
       carried across iterations the loop gain exceeded one and the layout oscillated with
       growing amplitude: 654 nodes reached 1e14 by the eightieth iteration and overflowed to
       NaN, which drew as an empty view. */
      const total = linkDegrees[source] + linkDegrees[target];
      const sourceShare = total === 0 ? 0.5 : linkDegrees[target] / total;
      const targetShare = total === 0 ? 0.5 : linkDegrees[source] / total;

      forces[source * 3] += deltaX * pull * sourceShare;
      forces[source * 3 + 1] += deltaY * pull * sourceShare;
      forces[source * 3 + 2] += deltaZ * pull * sourceShare;
      forces[target * 3] -= deltaX * pull * targetShare;
      forces[target * 3 + 1] -= deltaY * pull * targetShare;
      forces[target * 3 + 2] -= deltaZ * pull * targetShare;
    }

    for (let index = 0; index < nodeCount; index++) {
      forces[index * 3] -= positions[index * 3] * gravity * alpha;
      forces[index * 3 + 1] -= positions[index * 3 + 1] * gravity * alpha;
      forces[index * 3 + 2] -= positions[index * 3 + 2] * gravity * alpha;
    }

    const maximumStep = linkDistance * MAXIMUM_STEP_FRACTION;

    for (let index = 0; index < nodeCount; index++) {
      /* The constrained axis is zeroed after the forces are summed rather than skipped while they
       are, so the planar solution is exactly the 3D one with one axis held - and the spring term
       still sees the true 3D distance, which is what keeps an edge between two planes from being
       over-shortened in the plane. */
      if (fixedZ) {
        velocities[index * 3 + 2] = 0;
        positions[index * 3 + 2] = fixedZ[index];
      }

      for (let axis = 0; axis < (fixedZ ? 2 : 3); axis++) {
        const at = index * 3 + axis;
        velocities[at] = (velocities[at] + forces[at]) * (1 - VELOCITY_DECAY);
      }

      /* Clamped as a vector rather than per axis, so limiting the step cannot also turn it. */
      const speed = Math.hypot(
        velocities[index * 3],
        velocities[index * 3 + 1],
        fixedZ ? 0 : velocities[index * 3 + 2]
      );
      const scale = speed > maximumStep ? maximumStep / speed : 1;

      positions[index * 3] += velocities[index * 3] * scale;
      positions[index * 3 + 1] += velocities[index * 3 + 1] * scale;
      if (!fixedZ) {
        positions[index * 3 + 2] += velocities[index * 3 + 2] * scale;
      }
    }
  };

  return {
    advance: steps => {
      const target = Math.min(iterations, settled + Math.max(0, steps));
      while (settled < target) {
        step();
        settled += 1;
      }
    },
    get remaining() {
      return iterations - settled;
    },
    toPositions: () =>
      Array.from({ length: nodeCount }, (_, index): Vec3 => [
        positions[index * 3],
        positions[index * 3 + 1],
        positions[index * 3 + 2],
      ]),
  };
};

/**
 * The settled layout, in one call.
 *
 * The shape every closed-form caller wants, and what this was before it became resumable: a
 * layout nobody is watching settle has no use for the intermediate steps.
 */
export const runForceLayout = (args: ForceLayoutArgs): Vec3[] => {
  const solve = createForceSolve(args);
  solve.advance(args.iterations);
  return solve.toPositions();
};
